package app.bloomey.web.components.widgets

import androidx.compose.runtime.Composable
import app.bloomey.web.theme.toSitePalette
import com.varabyte.kobweb.compose.css.CSSTransition
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.TransitionProperty
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.silk.components.icons.ChevronLeftIcon
import com.varabyte.kobweb.silk.components.icons.ChevronRightIcon
import com.varabyte.kobweb.silk.components.style.ComponentStyle
import com.varabyte.kobweb.silk.components.style.toModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

val PageButtonStyle by ComponentStyle {
    cssRule(" span") {
        Modifier
            .fontSize(FontSize.Small)
            .transition(CSSTransition(TransitionProperty.All, duration = 300.ms))
    }
    cssRule(" span:hover") {
        Modifier
            .scale(80.percent)
            .color(colorMode.toSitePalette().brand.primary)
    }
}

@Composable
fun PaginationController(
    modifier: Modifier = Modifier,
    pageCount: Int,
    currentPage: Int,
    windowSize: Int = 4,
    onPageClicked: (Int) -> Unit,
) {
    Row(
        modifier = modifier.height(26.px).gap(12.px),
        verticalAlignment = Alignment.CenterVertically
    ) {
        ChevronBox({
            ChevronLeftIcon(modifier = it)
        }) {
            if (currentPage > 1) {
                onPageClicked(currentPage - 1)
            }
        }
        Row(
            modifier = Modifier.fillMaxHeight().outline(style = LineStyle.Solid, width = .4.px)
                .borderRadius(26.px)
        ) {
            val halfWindow = (windowSize / 2)
            val windowSizePlus = windowSize + 1
            val windowSizeMinus = windowSize - 1

            val rangeEndInclusive = pageCount + 1

            var rangeStart = (currentPage - halfWindow)
                .coerceAtLeast(1)


            var rangeEnd = when {
                rangeStart == 1 -> rangeStart + windowSizePlus
                else -> (currentPage + windowSizeMinus)
                    .coerceAtMost(rangeEndInclusive)
            }

            val matchRange = rangeEnd == rangeStart
            when {
                (rangeEnd - rangeStart) < halfWindow -> {
                    rangeStart -= halfWindow
                }

                matchRange && rangeEnd < pageCount -> {
                    rangeEnd++
                }

                matchRange && rangeStart == pageCount -> {
                    rangeStart--
                }

                rangeEnd >= rangeEndInclusive -> {
                    rangeStart = rangeEndInclusive - windowSizePlus
                }
            }

            val visibleRange = (rangeStart
                .coerceAtLeast(1) until rangeEnd.coerceAtMost(rangeEndInclusive))
                .let {
                    if (1 in it) {
                        it.take(windowSizePlus)
                    } else
                        it.take(windowSize)
                }

            if (1 !in visibleRange) {
                PageButton(1, currentPage, onPageClicked)
            }
            visibleRange.map { page ->
                PageButton(page, currentPage, onPageClicked)
            }
            if (pageCount !in visibleRange) {
                PageButton(pageCount, currentPage, onPageClicked)
            }
        }
        ChevronBox({
            ChevronRightIcon(modifier = it)
        }) {
            if (currentPage < pageCount) {
                onPageClicked(currentPage + 1)
            }
        }
    }
}

@Composable
private fun PageButton(
    page: Int,
    currentPage: Int,
    onPageClicked: (Int) -> Unit,
) {
    val sitePalette = ColorMode.current.toSitePalette()
    val activePage = page == currentPage
    Box(
        modifier = Modifier
            .fillMaxHeight()
            .size(26.px)
            .minWidth(26.px)
            .thenIf(
                activePage,
                Modifier.color(sitePalette.brand.onPrimary)
                    .background(sitePalette.brand.primary)
            )
            .borderRadius(50.percent)
            .cursor(Cursor.Pointer)
            .onClick {
                onPageClicked(page)
            }
            .then(PageButtonStyle.toModifier()),
        contentAlignment = Alignment.Center
    ) {

        SpanText("$page")
    }
}

@Composable
private fun ChevronBox(content: @Composable (Modifier) -> Unit, onClick: () -> Unit) {
    val sitePalette = ColorMode.current.toSitePalette()
    Box(
        modifier = Modifier
            .fillMaxHeight()
            .minWidth(26.px)
            .borderRadius(50.percent)
            .outline(style = LineStyle.Solid, width = .8.px)
            .cursor(Cursor.Pointer)
            .onClick {
                onClick()
            },
        contentAlignment = Alignment.Center
    ) {
        content(
            Modifier
                .size(50.percent)
                .color(sitePalette.brand.primary)
        )
    }
}